// POS-I226: per-user appearance choices (text size, density, colour). Theme keeps its own store.
// The five colour presets are tuned so white text on the primary reaches 4.5:1 and the primary on the
// sidebar background reaches 3:1 in both themes (appearance.spec.ts asserts both and that index.css agrees).

export type FontSize = 'small' | 'medium' | 'large';
export type Density = 'comfortable' | 'compact';
export type AccentName = 'default' | 'blue' | 'green' | 'purple' | 'orange' | 'rose';
export type ThemeChoice = 'light' | 'dark' | 'system';

export interface UiPreferences {
  theme?: ThemeChoice;
  density?: Density;
  fontSize?: FontSize;
  accent?: AccentName;
}

export const FONT_SIZES: FontSize[] = ['small', 'medium', 'large'];
export const DENSITIES: Density[] = ['comfortable', 'compact'];
export const ACCENTS: AccentName[] = ['default', 'blue', 'green', 'purple', 'orange', 'rose'];
export const THEMES: ThemeChoice[] = ['light', 'dark', 'system'];

export const DEFAULT_APPEARANCE = { density: 'comfortable', fontSize: 'medium', accent: 'default' } as const;

/** "H S% L%" primary of each preset (the same value in light and dark). 'default' keeps the built-in tokens. */
export const ACCENT_PRESETS: Record<Exclude<AccentName, 'default'>, string> = {
  blue: '217 91% 48%',
  green: '142 71% 29%',
  purple: '271 81% 56%',
  orange: '24 95% 38%',
  rose: '346 77% 47%',
};

/** The swatch colour shown in Settings for every accent (default = the built-in indigo). */
export const ACCENT_SWATCH: Record<AccentName, string> = { default: '234 89% 60%', ...ACCENT_PRESETS };

export function isFontSize(v: unknown): v is FontSize { return FONT_SIZES.includes(v as FontSize); }
export function isDensity(v: unknown): v is Density { return DENSITIES.includes(v as Density); }
export function isAccent(v: unknown): v is AccentName { return ACCENTS.includes(v as AccentName); }
export function isThemeChoice(v: unknown): v is ThemeChoice { return THEMES.includes(v as ThemeChoice); }

/** Puts the choices on <html>; the rules live in index.css. Medium / comfortable / default remove the attribute. */
export function applyAppearanceToRoot(
  root: HTMLElement,
  p: { fontSize: FontSize; density: Density; accent: AccentName },
): void {
  const set = (name: string, value: string, fallback: string) => {
    if (value === fallback) root.removeAttribute(name);
    else root.setAttribute(name, value);
  };
  set('data-font', p.fontSize, 'medium');
  set('data-density', p.density, 'comfortable');
  set('data-accent', p.accent, 'default');
}
